"use client";

import type { PostOriginRequest } from "@/generated/fastapi";
import { AudioTranscriptInputFlow } from "@/features/audio-input";
import { showErrorToast } from "@/shared/feedback";
import useTranscribeAudioMutation from "../mutations/useTranscribeAudioMutation";

interface OriginAudioFileCreateFlowProps {
  isSubmittingTranscript?: boolean;
  readyMessage?: string;
  sourcedWhere: NonNullable<PostOriginRequest["sourced_where"]>;
  transcribeAndSubmitLabel?: string;
  transcriptValue: string;
  onTranscriptChange: (nextValue: string) => void;
  onTranscriptReset: () => void;
  onTranscriptSubmit?: (nextValue: string) => Promise<void> | void;
}

export default function OriginAudioFileCreateFlow({
  isSubmittingTranscript = false,
  readyMessage = "Transcript received. You can edit it below before adding the source material.",
  sourcedWhere,
  transcribeAndSubmitLabel,
  transcriptValue,
  onTranscriptChange,
  onTranscriptReset,
  onTranscriptSubmit,
}: OriginAudioFileCreateFlowProps) {
  const transcribeAudioMutation = useTranscribeAudioMutation();
  const transcriptionMeta =
    transcribeAudioMutation.data?.transcription_meta ?? null;

  const handleTranscribe = async (selectedFile: File) => {
    try {
      const result = await transcribeAudioMutation.mutateAsync({
        selectedFile,
        sourcedWhere,
      });

      onTranscriptChange(result.transcribed_text ?? "");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to transcribe audio";
      showErrorToast(message);
    }
  };

  const handleTranscribeAndSubmit = async (selectedFile: File) => {
    try {
      const result = await transcribeAudioMutation.mutateAsync({
        selectedFile,
        sourcedWhere,
      });

      await onTranscriptSubmit?.(result.transcribed_text ?? "");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to transcribe audio";
      showErrorToast(message);
    }
  };

  const handleReset = () => {
    transcribeAudioMutation.reset();
    onTranscriptReset();
  };

  return (
    <AudioTranscriptInputFlow
      errorMessage={transcribeAudioMutation.error?.message}
      isError={transcribeAudioMutation.isError}
      isTranscribing={transcribeAudioMutation.isPending || isSubmittingTranscript}
      mode="audioFile"
      readyMessage={readyMessage}
      transcribeAndSubmitLabel={transcribeAndSubmitLabel}
      transcribeAndSubmitLoadingText="Adding"
      transcriptValue={transcriptValue}
      transcriptionMeta={transcriptionMeta}
      onReset={handleReset}
      onTranscribe={handleTranscribe}
      onTranscribeAndSubmit={
        onTranscriptSubmit ? handleTranscribeAndSubmit : undefined
      }
    />
  );
}
